import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCount } from '@app/src/api/fetchHooks'
import useSignalRListener from '@app/src/hooks/signalRListener'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderEnrichmentStatus } from '@app/src/types/organizations'
import { ResponseScrapingStatus, SolutionType } from '@app/src/types/resourceExplorer'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import type { AiJob, AiJobType, CompletedJob } from './AiJobsContext'
import AiJobsContext from './AiJobsContext'
import { useAuthentication } from './AuthenticationContext'

const COUNT_KEYS: Record<AiJobType, [FetchKey, string]> = {
  screening: [FetchKey.JobRunning, 'screening'],
  enrichment: [FetchKey.JobRunning, 'enrichment'],
}

const ACTIVE_POLL_INTERVAL = 5000
const IDLE_POLL_INTERVAL = 30000
const POLLING_COOLDOWN_MS = 5000
const INVALIDATION_DEBOUNCE_MS = 2000

type AiJobsProviderProps = {
  children: ReactNode
}

const AiJobsContextProvider: React.FC<AiJobsProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const [isPollingActive, setIsPollingActive] = useState(true)
  const { scope } = useAuthentication()
  const enabled = Boolean(scope.organization && scope.solution !== SolutionType.Default.toLowerCase())
  const [jobs, setJobs] = useState<AiJob[]>([])
  const [completedJobs, setCompletedJobs] = useState<CompletedJob[]>([])

  const jobsRef = useRef(jobs)
  jobsRef.current = jobs

  const addJob = useCallback(
    (job: Omit<AiJob, 'id' | 'startedAt'>) => {
      setJobs(prev => {
        const filtered = prev.filter(j => j.type !== job.type)
        return [...filtered, { ...job, id: `${job.type}-${Date.now()}`, startedAt: Date.now() }]
      })
      setIsPollingActive(true)
      queryClient.invalidateQueries({ queryKey: COUNT_KEYS[job.type] })
    },
    [queryClient],
  )

  const markJobComplete = useCallback((type: AiJobType) => {
    setJobs(prev => {
      const job = prev.find(j => j.type === type)
      if (job) {
        setCompletedJobs(prevCompleted => {
          const filtered = prevCompleted.filter(j => j.type !== type)
          return [
            ...filtered,
            {
              id: `${type}-completed-${Date.now()}`,
              type,
              completedAt: Date.now(),
              enrichedProviderIds: job.providerIds,
            },
          ]
        })
      }
      return prev.filter(j => j.type !== type)
    })
  }, [])

  const markJobAsViewed = useCallback((jobId: string) => {
    setCompletedJobs(prev => prev.filter(job => job.id !== jobId))
  }, [])

  const clearCompletedJobs = useCallback(() => {
    setCompletedJobs([])
  }, [])

  const { count: screeningCount, dataUpdatedAt: screeningCountUpdatedAt } = useFetchCount({
    key: COUNT_KEYS.screening,
    endpoint: endpoints.responsesScrapingCount,
    payload: [{ name: 'status', filters: [{ operator: Operators.EqualTo, value: ResponseScrapingStatus.InProgress }] }],
    shouldShowErrorNotification: false,
    options: {
      refetchInterval: isPollingActive ? ACTIVE_POLL_INTERVAL : IDLE_POLL_INTERVAL,
      staleTime: ACTIVE_POLL_INTERVAL,
      enabled: enabled,
    },
  })

  const { count: enrichmentCount, dataUpdatedAt: enrichmentCountUpdatedAt } = useFetchCount({
    key: COUNT_KEYS.enrichment,
    endpoint: endpoints.providerCount,
    payload: [
      {
        name: 'enrichments.status',
        filters: [{ operator: Operators.EqualTo, value: ProviderEnrichmentStatus.InProgress }],
      },
    ],
    shouldShowErrorNotification: false,
    options: {
      refetchInterval: isPollingActive ? ACTIVE_POLL_INTERVAL : IDLE_POLL_INTERVAL,
      staleTime: ACTIVE_POLL_INTERVAL,
      enabled: enabled,
    },
  })

  const invalidateScreeningQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseScraping] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseScrapingFacets] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseScrapingCount] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.Response] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.AllResponses] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.FindingsProviders] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseItemsCollection] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ResponseItemsFacets] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.AssessmentCollection] })
  }, [queryClient])

  const invalidateProviderQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [FetchKey.Provider] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderFacets] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.AllProviders] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCount] })
  }, [queryClient])

  const syncJobWithCount = useCallback(
    (type: AiJobType, count: number | undefined, countUpdatedAt: number, onFinished: () => void) => {
      if (count === undefined) return
      const job = jobsRef.current.find(j => j.type === type)

      if (count > 0) {
        if (!job) {
          setJobs(prev => [
            ...prev,
            { type, id: `${type}-${Date.now()}`, startedAt: Date.now(), inProgressCount: count },
          ])
          setIsPollingActive(true)
        } else if (job.inProgressCount !== count) {
          setJobs(prev => prev.map(j => (j.type === type ? { ...j, inProgressCount: count } : j)))
        }
      } else if (job && countUpdatedAt > job.startedAt) {
        markJobComplete(type)
        onFinished()
      }
    },
    [markJobComplete],
  )

  useEffect(() => {
    syncJobWithCount('screening', screeningCount, screeningCountUpdatedAt, invalidateScreeningQueries)
  }, [screeningCount, screeningCountUpdatedAt, syncJobWithCount, invalidateScreeningQueries])

  useEffect(() => {
    syncJobWithCount('enrichment', enrichmentCount, enrichmentCountUpdatedAt, invalidateProviderQueries)
  }, [enrichmentCount, enrichmentCountUpdatedAt, syncJobWithCount, invalidateProviderQueries])

  const onScreeningBatchCompleted = useDebouncedCallback(() => {
    invalidateScreeningQueries()
    invalidateProviderQueries()
    queryClient.invalidateQueries({ queryKey: COUNT_KEYS.screening })
  }, INVALIDATION_DEBOUNCE_MS)

  const onEnrichmentBatchCompleted = useDebouncedCallback(() => {
    invalidateProviderQueries()
    queryClient.invalidateQueries({ queryKey: COUNT_KEYS.enrichment })
  }, INVALIDATION_DEBOUNCE_MS)

  const onProviderImportCompleted = useDebouncedCallback(invalidateProviderQueries, INVALIDATION_DEBOUNCE_MS)

  useSignalRListener('AiScrapingBatchCompleted', onScreeningBatchCompleted)

  useSignalRListener('ProviderEnrichmentBatchCompleted', onEnrichmentBatchCompleted)

  useSignalRListener('ProviderImportCompleted', onProviderImportCompleted)

  const isAnyJobRunning = jobs.length > 0

  useEffect(() => {
    if (isAnyJobRunning) {
      setIsPollingActive(true)
    } else {
      const timeoutId = setTimeout(() => setIsPollingActive(false), POLLING_COOLDOWN_MS)
      return () => clearTimeout(timeoutId)
    }
  }, [isAnyJobRunning])

  const contextValue = useMemo(
    () => ({ jobs, completedJobs, isAnyJobRunning, addJob, markJobAsViewed, clearCompletedJobs }),
    [jobs, completedJobs, isAnyJobRunning, addJob, markJobAsViewed, clearCompletedJobs],
  )

  return <AiJobsContext.Provider value={contextValue}>{children}</AiJobsContext.Provider>
}

export default AiJobsContextProvider
